import React, { createContext, useCallback, useContext, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';

import { Dispatch, store } from 'src/config/rootStore';
import { isOnboardingCompleted } from 'src/modules/onboarding/selectors/onboardingSelectors';
import { getUserRole } from 'src/modules/user/selectors/userSelectors';
import { shouldShowPreSessionAssessments } from 'src/modules/videoSessions/selectors/videoSessionsSelectors';
import { useShouldTakeToRequestPersonalEmailAddressFlowRequiredOnly } from 'src/modules/user/hooks/userHooks';

import { getRedirectFrom, isAuthenticated } from '../selectors/authenticationSelectors';
import { isInitialDataFetched } from '../selectors/isInitialDataFetched';
import { useFetchInitialData } from '../fetch/initialData';
import { Role } from 'src/modules/user/models/user';

const AppInitializationContext = createContext<boolean>(false);

export const AppContext: React.FC = props => {
    const initialDataFetched = useSelector(isInitialDataFetched);
    const authenticated = useSelector(isAuthenticated);
    const navigateToNextPage = useNextPage();

    useFetchInitialData(authenticated);

    useEffect(() => {
        if (authenticated && initialDataFetched) {
            navigateToNextPage();
        }
    }, [authenticated, initialDataFetched, navigateToNextPage]);

    // If we should re-validate authentication data trigger that to
    // determine the user's authentication state...
    useEffect(() => {
        store.dispatch.authentication.validateAuthData();
    }, []);

    return (
        <AppInitializationContext.Provider value={authenticated && initialDataFetched}>
            {props.children}
        </AppInitializationContext.Provider>
    );
};

export const AppInitializationContextProvider = AppInitializationContext.Provider;

export const useIsAppInitialized = () => useContext(AppInitializationContext);

/**
 * Only use this hook if your route _must_ always override the user's preferred destination.
 *
 * _Do not_ override the user's destination if the task is not required for them to continue
 * using Modern Health.
 */
function useNextPage() {
    const redirectFrom = useSelector(getRedirectFrom);
    const location = useLocation();
    // We only want to redirect the user once per app initialization.
    const redirected = useRef(false);
    const userRole = useSelector(getUserRole);
    const sessionUuidForPreSessionAssessments = useSelector(shouldShowPreSessionAssessments);
    const shouldTakeToPersonalEmailAddressFlowOutsideOnboarding =
        useShouldTakeToRequestPersonalEmailAddressFlowRequiredOnly();
    const dispatch = useDispatch<Dispatch>();
    const history = useHistory();
    const onboardingCompleted = useSelector(isOnboardingCompleted);

    const resolveNavigationOverride = useCallback(() => {
        if (userRole !== Role.MEMBER) {
            return null;
        }

        if (!onboardingCompleted) {
            return { nextPage: '/onboarding' };
        }

        if (sessionUuidForPreSessionAssessments) {
            return {
                nextPage: '/pre-session-assessments-intro',
                nextPageState: { sessionUuid: sessionUuidForPreSessionAssessments },
            };
        }

        if (shouldTakeToPersonalEmailAddressFlowOutsideOnboarding) {
            return { nextPage: '/preferred-email' };
        }

        return null;
    }, [
        onboardingCompleted,
        userRole,
        sessionUuidForPreSessionAssessments,
        shouldTakeToPersonalEmailAddressFlowOutsideOnboarding,
    ]);

    return useCallback(() => {
        if (redirected.current) return;
        redirected.current = true;

        const next = resolveNavigationOverride();
        if (!next) {
            if (redirectFrom) {
                dispatch.authentication.setRedirectFrom(null);

                history.push(redirectFrom);
            }

            return;
        }

        const { nextPage, nextPageState } = next;

        if (location.pathname === nextPage) return;

        if (nextPage && nextPageState) {
            history.replace(nextPage, nextPageState);
            return;
        }

        history.replace(nextPage);
    }, [
        resolveNavigationOverride,
        location.pathname,
        history,
        redirectFrom,
        dispatch.authentication,
    ]);
}
